import { useState, useEffect } from 'react';

export type CookiePreferences = {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  personalized: boolean;
};

const DEFAULT_PREFERENCES: CookiePreferences = {
  necessary: true, // Always true
  analytics: true,
  marketing: false,
  personalized: false,
};

export function useCookieConsent() {
  const [preferences, setPreferences] = useState<CookiePreferences>(DEFAULT_PREFERENCES);
  const [hasConsented, setHasConsented] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  useEffect(() => {
    // Load from local storage on mount
    const saved = localStorage.getItem('vexl_cookie_preferences');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setPreferences({ ...DEFAULT_PREFERENCES, ...parsed, necessary: true });
        setHasConsented(true);
      } catch (e) {
        console.error("Failed to parse cookie preferences");
      }
    } else {
      // If no preferences saved, we should show the modal (or a banner)
      // For a non-intrusive approach, we can just let them click 'Manage Cookies' from footer
      // or pop it up after a few seconds.
    }
  }, []);

  const savePreferences = (newPrefs: CookiePreferences) => {
    // Force necessary to always be true
    const toSave = { ...newPrefs, necessary: true };
    localStorage.setItem('vexl_cookie_preferences', JSON.stringify(toSave));
    setPreferences(toSave);
    setHasConsented(true);
    setIsModalOpen(false);
    
    // Here you would typically trigger/block actual scripts (GTM, Meta Pixel, etc.)
    // based on the boolean values in `toSave`.
  };

  const acceptAll = () => {
    savePreferences({
      necessary: true,
      analytics: true,
      marketing: true,
      personalized: true,
    });
  };

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  return {
    preferences,
    hasConsented,
    isModalOpen,
    openModal,
    closeModal,
    savePreferences,
    acceptAll
  };
}
